import { cva } from "class-variance-authority";

/**
 * The roles Switch offers for its "on" track, and the source of `SwitchProps["color"]`. `muted` and
 * `white` are omitted because they read as off; `secondary` because its token (90% gray) is
 * indistinguishable from the `--input` off track.
 */
export const SWITCH_ROLES = ["primary", "destructive", "success", "warning", "info", "ai"] as const;

export type SwitchColor = (typeof SWITCH_ROLES)[number];

// Every class is spelled out longhand rather than composed from the shared `FILL_ROLES` registry:
// the role's fill only applies behind `data-[state=checked]:`, and Tailwind's scanner cannot see a
// prefix added at runtime, so the gated form has to exist literally in source.
export const switchVariants = cva(
  "peer inline-flex h-5 w-8 shrink-0 items-center rounded-full border border-transparent transition-all outline-none " +
    "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 " +
    "disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      color: {
        primary: "data-[state=checked]:bg-primary",
        destructive: "data-[state=checked]:bg-destructive",
        success: "data-[state=checked]:bg-success",
        warning: "data-[state=checked]:bg-warning",
        info: "data-[state=checked]:bg-info",
        ai: "data-[state=checked]:bg-ai",
      } satisfies Record<SwitchColor, string>,
      offVariant: {
        default: "data-[state=unchecked]:bg-input dark:data-[state=unchecked]:bg-input/80",
        inverted: "data-[state=unchecked]:bg-background",
      },
    },
    defaultVariants: {
      color: "primary",
      offVariant: "default",
    },
  }
);

export const switchThumbVariants = cva(
  "pointer-events-none block size-4 rounded-full ring-0 transition-transform " +
    "data-[state=checked]:translate-x-[calc(100%-2px)] data-[state=unchecked]:translate-x-0",
  {
    variants: {
      color: {
        primary: "data-[state=checked]:bg-primary-foreground",
        destructive: "data-[state=checked]:bg-destructive-foreground",
        success: "data-[state=checked]:bg-success-foreground",
        warning: "data-[state=checked]:bg-warning-foreground",
        info: "data-[state=checked]:bg-info-foreground",
        ai: "data-[state=checked]:bg-ai-foreground",
      } satisfies Record<SwitchColor, string>,
      offVariant: {
        default: "data-[state=unchecked]:bg-background dark:data-[state=unchecked]:bg-foreground",
        inverted: "data-[state=unchecked]:bg-input dark:data-[state=unchecked]:bg-input/80",
      },
    },
    defaultVariants: {
      color: "primary",
      offVariant: "default",
    },
  }
);
